import { AlmanacEvent, AlmanacCategory } from '../types';
import { generateAlmanacEvents } from './geminiService';

// MOCK DATASET - STATIC CORE (The "Curated" List)
const MOCK_EVENTS: AlmanacEvent[] = [
  {
    id: 'AI_1950_TURING',
    date: '1950-10-01',
    year: 1950,
    title: 'Computing Machinery and Intelligence',
    description: 'Alan Turing pubblica il paper che introduce il "Turing Test".',
    category: 'AI_MILESTONE',
    source: 'AI_WATCH',
    confidenceScore: 1.0,
    impactLevel: 'Critical',
    tags: ['Foundational', 'Philosophy'],
    relatedEvents: ['AI_1956_DARTMOUTH']
  },
  {
    id: 'INC_1988_MORRIS',
    date: '1988-11-02',
    year: 1988,
    title: 'Il Morris Worm',
    description: 'Il primo worm diffuso via Internet blocca il 10% della rete.',
    category: 'INCIDENT',
    source: 'CSIS',
    confidenceScore: 1.0,
    impactLevel: 'High',
    tags: ['Malware', 'History'],
    relatedEvents: []
  },
  {
    id: 'REG_2018_GDPR',
    date: '2018-05-25',
    year: 2018,
    title: 'GDPR Enforcement',
    description: 'Entra in vigore il Regolamento Generale sulla Protezione dei Dati.',
    category: 'REGULATION',
    source: 'INTERNAL',
    confidenceScore: 1.0,
    impactLevel: 'High',
    tags: ['Privacy', 'Legal']
  }
];

// Cache per evitare troppe chiamate API per la stessa data
const eventCache: Record<string, AlmanacEvent[]> = {};

export const getAlmanacEvents = async (filters?: {
  category?: AlmanacCategory;
  year?: number;
  source?: string;
  search?: string;
}): Promise<AlmanacEvent[]> => {
  await new Promise(r => setTimeout(r, 400)); // Sim network

  let filtered = [...MOCK_EVENTS];

  // Merge with cached AI events if needed
  Object.values(eventCache).forEach(cachedList => {
      filtered = [...filtered, ...cachedList];
  });

  // Deduplicate by ID
  filtered = Array.from(new Map(filtered.map(item => [item.id, item])).values());

  if (filters?.category) {
    filtered = filtered.filter(e => e.category === filters.category);
  }
  if (filters?.year) {
    filtered = filtered.filter(e => e.year === filters.year);
  }
  if (filters?.source) {
    filtered = filtered.filter(e => e.source === filters.source);
  }
  if (filters?.search) {
    const q = filters.search.toLowerCase();
    filtered = filtered.filter(e => 
      e.title.toLowerCase().includes(q) || 
      e.description.toLowerCase().includes(q) ||
      e.tags.some(t => t.toLowerCase().includes(q))
    );
  }

  return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export const getEventsByMonth = (month: number): AlmanacEvent[] => {
  // Return mixed static and cached events
  const allEvents = [...MOCK_EVENTS, ...Object.values(eventCache).flat()];
  const uniqueEvents = Array.from(new Map(allEvents.map(item => [item.id, item])).values());

  return uniqueEvents.filter(e => {
    const d = new Date(e.date);
    return d.getMonth() === month;
  }).sort((a, b) => {
    const da = new Date(a.date);
    const db = new Date(b.date);
    return da.getDate() - db.getDate();
  });
};

export const fetchDailyIntel = async (month: number, day: number): Promise<AlmanacEvent[]> => {
    const cacheKey = `${month}-${day}`;
    
    // 1. Check Cache first
    if (eventCache[cacheKey]) {
        return eventCache[cacheKey];
    }

    // 2. Check Static Data
    const staticMatches = MOCK_EVENTS.filter(e => {
        const d = new Date(e.date);
        return d.getMonth() === month && d.getDate() === day;
    });

    if (staticMatches.length > 0) {
        return staticMatches;
    }

    // 3. Ask AI (Simulate API Call to MITRE/NIST)
    try {
        const aiEvents = await generateAlmanacEvents(month, day);
        if (aiEvents && aiEvents.length > 0) {
            eventCache[cacheKey] = aiEvents;
            return aiEvents;
        }
    } catch (e) {
        console.warn("Failed to fetch live intel");
    }

    return [];
};

export const getHeatmapData = (): { year: number; count: number }[] => {
  const map: Record<number, number> = {};
  const allEvents = [...MOCK_EVENTS, ...Object.values(eventCache).flat()];
  
  allEvents.forEach(e => {
    map[e.year] = (map[e.year] || 0) + 1;
  });
  
  const years = Object.keys(map).map(Number).sort((a, b) => a - b);
  return years.map(year => ({ year, count: map[year] }));
};

export const getThisWeekEvents = (): AlmanacEvent[] => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();
  return MOCK_EVENTS.filter(e => {
    const d = new Date(e.date);
    return d.getMonth() === currentMonth && Math.abs(d.getDate() - currentDay) <= 7;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};
